import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { NotificationBell } from '@/components/NotificationBell';
import { PushNotificationButton } from '@/components/PushNotificationButton';
import { TrendingUp, Video, FileText, Settings, LogOut, Wifi, Calendar, MessageSquare, Menu, X } from 'lucide-react';

export const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/'); setMenuOpen(false); };
  const isActive = (path) => location.pathname === path;

  const isOperativa = user?.subscription_type === 'operativa';

  const allNavItems = [
    { path: '/social', icon: MessageSquare, label: 'Social' },
    { path: '/dashboard', icon: TrendingUp, label: 'Señales' },
    { path: '/videos', icon: Video, label: 'Videos' },
    { path: '/resources', icon: FileText, label: 'Recursos' },
    { path: '/economic-news', icon: Calendar, label: 'Noticias' },
  ];

  // Operativa mode: only show EN VIVO
  const navItems = isOperativa ? [] : allNavItems;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-emerald-500/20">
      <div className="container mx-auto px-3 sm:px-6 py-2 sm:py-3">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center space-x-2 flex-shrink-0" data-testid="navbar-logo" onClick={() => setMenuOpen(false)}>
            <img src="/logo.png" alt="Logo" className="w-7 h-7 sm:w-8 sm:h-8 object-contain" />
            <span className="text-xs sm:text-sm font-bold tracking-wider hidden md:block" style={{ fontFamily: 'Rajdhani, sans-serif' }}>TRADING ALIENÍGENA</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-1">
            {navItems.map(({ path, icon: Icon, label }) => (
              <Link key={path} to={path}>
                <Button variant="ghost" size="sm" className={`text-xs ${isActive(path) ? 'text-emerald-400 bg-emerald-500/10' : 'text-emerald-100/70 hover:text-emerald-400'}`}>
                  <Icon className="h-3.5 w-3.5 mr-1" />{label}
                </Button>
              </Link>
            ))}
            <Link to="/live">
              <Button variant="ghost" size="sm" className={`text-xs ${isActive('/live') ? 'text-emerald-400 bg-emerald-500/10' : 'text-emerald-400 pulse-glow'}`}>
                <Wifi className="h-3.5 w-3.5 mr-1" />EN VIVO
              </Button>
            </Link>
            <PushNotificationButton />
            <NotificationBell />
            {user?.role === 'admin' && (
              <Link to="/admin/signals">
                <Button variant="ghost" size="sm" className="text-yellow-400 hover:text-yellow-300 text-xs">
                  <Settings className="h-3.5 w-3.5 mr-1" />Admin
                </Button>
              </Link>
            )}
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-emerald-100/70 hover:text-red-400">
              <LogOut className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Mobile: compact horizontal icons + hamburger */}
          <div className="flex lg:hidden items-center flex-row gap-1">
            <Link to="/live">
              <button className="p-1.5 text-emerald-400 pulse-glow rounded-md">
                <Wifi className="h-4 w-4" />
              </button>
            </Link>
            <PushNotificationButton />
            <NotificationBell />
            <button onClick={() => setMenuOpen(!menuOpen)} className="text-emerald-400 p-1.5" data-testid="mobile-menu-btn">
              {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown */}
        {menuOpen && (
          <div className="lg:hidden mt-2 pb-2 border-t border-emerald-500/20 pt-2 space-y-0.5" data-testid="mobile-menu">
            {navItems.map(({ path, icon: Icon, label }) => (
              <Link key={path} to={path} onClick={() => setMenuOpen(false)} className="block">
                <div className={`flex items-center space-x-3 px-3 py-2 rounded-lg ${isActive(path) ? 'bg-emerald-500/10 text-emerald-400' : 'text-emerald-100/70'}`}>
                  <Icon className="h-4 w-4" /><span className="text-sm">{label}</span>
                </div>
              </Link>
            ))}
            <Link to="/live" onClick={() => setMenuOpen(false)} className="block">
              <div className={`flex items-center space-x-3 px-3 py-2 rounded-lg ${isActive('/live') ? 'bg-emerald-500/10 text-emerald-400' : 'text-emerald-400'}`}>
                <Wifi className="h-4 w-4" /><span className="text-sm font-bold">EN VIVO</span>
              </div>
            </Link>
            {user?.role === 'admin' && (
              <Link to="/admin/signals" onClick={() => setMenuOpen(false)} className="block">
                <div className="flex items-center space-x-3 px-3 py-2 rounded-lg text-yellow-400">
                  <Settings className="h-4 w-4" /><span className="text-sm">Admin</span>
                </div>
              </Link>
            )}
            <button onClick={handleLogout} className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-red-400">
              <LogOut className="h-4 w-4" /><span className="text-sm">Cerrar Sesión</span>
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};
